<template lang="pug">
.index-wrapper.w-full.h-full.bg-no-repeat.bg-cover.bg-center.overflow-hidden(:style="`--horizontalBg:url(${getBackCdnUrl('icon/pwa_login.png')})`")
  .index-content.relative.w-full.h-full.overflow-y-auto
    .index-grid.relative.grid.h-full.m-auto
      .area-header.text-primary-text
        .navigator.flex.justify-between.text-xs.items-center
          .block.flex.justify-end.items-center.h-8
            a.button.flex.justify-center.items-center.h-full.px-4.me-2.font-bold(v-if="config.public.indexHomeBtn === 'true'", :href="official", class="md:me-3") {{ $t('common.home') }}
            LangCurrencySwitcher.whitespace-nowrap.cursor-pointer.me-2(v-if="isMirror")
            LangSwitcher.whitespace-nowrap.cursor-pointer.me-2(v-else, class="md:me-3")
          .block(v-if="isClient && downloadVisible && !hasFriendCode")
            a.button.block.py-2.px-3.whitespace-nowrap.animate-bounce.text-button-text(:href="getDownloadLink", data-location="home", data-icon="DownloadApp", target="_blank", rel="nofollow me noopener noreferrer", @click="setDownloadGuide")
              img.inline-block.w-4.align-middle(src="/common/ios-download-icon.svg", alt="ios")
              img.inline-block.w-4.align-middle(src="/common/android-download-icon.svg", alt="android")
              span.inline-block.ms-1.align-middle.text-button-text.font-bold {{ $t('common.download') }}
      .area-left.self-center(class="h-8/10 6xl:h-11/15 max-h-[276px] 2xl:max-h-[376px] 5xl:max-h-[537px] aspect-[44/69] portrait:hidden")
        LazyBanner(v-if="isClient && loginPageAdBlock.leftBlock && bannerList.length", :list="bannerList", :redirect="false", :pagination="true", @showCustomerService="showCustomerService", @click="open('Login')")
      .area-main.place-self-center.flex.flex-col.justify-center.items-center
        .area-main-wrapper.w-full
          img.area-main-logo.mx-auto(class="w-264/360 6xl:w-4/5 portrait:xs:w-2/3 aspect-[1240/640]", :src="getBackCdnUrl('icon/pwa_big_logo.png')", alt="logo")
          LazyBannerInformation.area-main-banner(v-if="isClient && loginPageAdBlock.centerBlock", class="w-5/6 6xl:w-11/12 mt-4 portrait:w-full", :sourceList="bannerList", @click="open('Login')")
          .area-main-button.flex.justify-center.items-start.gap-2.mt-3.mx-auto(class="w-5/6 xl:mt-6 portrait:mt-6 6xl:gap-x-5 flex-wrap xxs:flex-nowrap portrait:gap-x-4")
            template(v-if="isClient")
              button(v-if="hasLogin", type="button", class="w-2/5 portrait:w-auto", @click="open('Login')")
                img(:src="getFrontCdnUrl('images/login/login_btn_login_{locale}_normal.png')", class="portrait:h-10", alt="login")
              button(v-if="hasRegister", type="button", class="w-2/5 portrait:w-auto", @click="open('SignUp')")
                img(:src="getFrontCdnUrl('images/login/login_btn_signup_{locale}_normal.png')", class="portrait:h-10", alt="register")
              button(v-if="hasGuest", type="button", class="w-2/5 portrait:w-auto", @click="enter('/guest')")
                img(:src="getFrontCdnUrl('images/login/login_btn_guest_{locale}_normal.png')", class="portrait:h-10", alt="guest")
      .area-right.justify-self-end.self-center(class="h-8/10 aspect-[5/9] 6xl:h-11/15 max-h-[276px] 2xl:max-h-[376px] 5xl:max-h-[537px] portrait:hidden")
        LazyProvidersRight.h-full(v-if="loginPageAdBlock.rightBlock && getProviderInfo?.length", :list="getProviderInfo", @click="open('Login')")
      .area-social.self-end
        LazySocialMediaList(v-if="isClient", :isWebView="isWebView", :socialList="socialMedia", :showSocialList="loginPageAdBlock.lowerLeftBlock")
      .area-terms.justify-self-center.self-end
        LazyPolicyLinks.mt-1(:resourceId="resourceId", :customizeResource="customizeResource", :siteName="config.public.name", @showPrivacy="showPrivacy")
      .area-service.place-self-end.z-10(class="landscape:relative")
        LazyCsMenu(v-if="showCsMenu", position="index", @close="closeCustomerService")
        button.block.w-12.h-12(v-show="isCustomerServiceEnable && !isTestUser", :class="'sm:w-15 sm:h-15 3xl:h-20 3xl:w-20'", type="button", @click="showCustomerService")
          img(v-if="getServiceImage", :src="getServiceImage")
          img(v-else, :src="getFrontCdnUrl('images/login/login_icon_service_{locale}_normal.png')", alt="service")
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'

import { useRuntimeConfig } from '#app'
import { useI18n } from '#imports'
import { useSite } from '@/composables/core/useSite'
import { useUserInfo } from '@/composables/user/useUserInfo'
import { useMainStore } from '@/stores/main'
import customerService from '@/utils/customerService'
import filterLocaleSetting from '@/utils/localeSetting'

const config = useRuntimeConfig()
const auth = useAuth()
const { activityList } = useActivity('index-original')
const { $SoundControl, $bridge } = useNuxtApp()
const { locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()
const mainStore = useMainStore()
const { userInfo } = useUserInfo({ autoInit: false })

const { getDownloadLink, downloadVisible, isWebView, getProviderInfo } = storeToRefs(mainStore)
const { setModal, setModalParams, setDownloadGuide } = mainStore

const { locale: defaultLocale, loginPageAdBlock, urls, csChat, socialMedia, hasLogin, hasRegister, hasGuest } = useSite()
const { getFrontCdnUrl, getBackCdnUrl } = useCdnAssets()

// Data
const resourceId = config.public.resourceId
const runningEnv = config.public.runningEnv
const showCsMenu = ref(false)
const isClient = ref(false)

// Computed
const isMirror = computed(() => {
  return urls.value?.mirror?.length > 0
})

const customizeResource = computed(() => {
  const licenseFilteredSites = {
    ONC: {
      'www.oncasino-000.com': 'oncasino-000',
      'www.oncasino-999.com': 'oncasino-999',
      'm.oncasino-000.com': 'oncasino-000',
      'm.oncasino-999.com': 'oncasino-999',
      '192.168.31.247:3000': 'oncasino-000',
    },
  }
  if (typeof window !== 'undefined') {
    const host = window.location.host
    return runningEnv === 'dev' || (licenseFilteredSites[resourceId] && licenseFilteredSites[resourceId]?.[host])
  } else {
    return null
  }
})

const hasFriendCode = computed(() => {
  return !!route?.query?.fc
})

const isTestUser = computed(() => {
  return userInfo.value?.scope === 'testuser'
})

const isCustomerServiceEnable = computed(() => {
  return !!csChat.value?.some((item) => item.enable)
})

const official = computed(() => {
  return urls.value?.official
})

const bannerList = computed(() => {
  return activityList.value.filter((campaign) => {
    if (campaign.activityTypeName?.toUpperCase() === 'PROMOTIONS') {
      return filterLocaleSetting(campaign.localeSetting, locale, defaultLocale, 'carouselUrl')
    } else {
      return campaign.carouselUrl?.[0]?.url || ''
    }
  })
})

const getServiceImage = computed(() => {
  if (!csChat.value) return false

  const enableCS = csChat.value.filter((item) => item.enable)
  // 如果只有開啟一個客服選項
  if (enableCS.length === 1) {
    return enableCS[0].image || getFrontCdnUrl('images/login/login_icon_service_{locale}_normal.png')
  }

  return false
})

// Methods
const autoLogin = async (data) => {
  const { provider, countryCode, phone, password, email } = data
  if (provider === 'phone') {
    await auth.loginWithPhone({ countryCode, phone, password })
  } else if (provider === 'email') {
    await auth.loginWithEmail({ email, password })
  }
}

const showCustomerService = () => {
  setTimeout(() => {
    const enableCS = csChat.value?.filter((item) => item.enable)

    if (!enableCS || enableCS.length === 0) {
      return
    }

    if (enableCS.length > 1) {
      showCsMenu.value = true
    } else {
      const firstCS = enableCS[0]
      if (firstCS.name === 'customermessage') {
        open('Login')
      } else {
        customerService.doAction(firstCS, 'setUser', ' ')
        customerService.doAction(firstCS, 'maximize')
      }
    }
  }, 25)
}

const closeCustomerService = () => {
  setTimeout(() => {
    showCsMenu.value = false
  }, 500)
}

const showPrivacy = (name) => {
  $SoundControl.sound.play('click')
  setModalParams({ policyName: name })
  setModal('ModalPrivacy')
}

const open = (panel) => {
  $SoundControl.sound.play('click')
  setModal(`ModalPanel${panel}`)
}

const enter = (path) => {
  navigateTo(localePath(path))
}

// Lifecycle
onMounted(async () => {
  isClient.value = true
  $SoundControl.background.play('index')

  if ($bridge?.available) {
    $bridge.callHandler('getAuth', null, (data) => {
      autoLogin(data)
    })
  }
})

onBeforeUnmount(() => {
  setModal(null) // close for login / signup modal
})

useHead({
  link: [
    { rel: 'preload', href: getBackCdnUrl('icon/pwa_big_logo.png'), as: 'image', fetchpriority: 'high' },
    { rel: 'preload', href: getBackCdnUrl('icon/pwa_login.png'), as: 'image' },
  ],
})
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

.index-wrapper {
  background-image: var(--horizontalBg);
}

.index-content {
  padding-top: max(env(safe-area-inset-top), 16px);
  padding-inline-end: max(env(safe-area-inset-right), 16px);
  padding-inline-start: max(env(safe-area-inset-left), 16px);
  padding-bottom: max(env(safe-area-inset-bottom), 16px);
}

@media (orientation: portrait) and (min-width: 736px) {
  .index-content {
    padding-top: max(env(safe-area-inset-top), 40px);
    padding-inline-end: max(env(safe-area-inset-right), 40px);
    padding-inline-start: max(env(safe-area-inset-left), 40px);
    padding-bottom: max(env(safe-area-inset-bottom), 40px);
  }
}

@media (min-width: 1024px) {
  .index-content {
    padding-top: max(env(safe-area-inset-top), 40px);
    padding-inline-end: max(env(safe-area-inset-right), 40px);
    padding-inline-start: max(env(safe-area-inset-left), 40px);
    padding-bottom: max(env(safe-area-inset-bottom), 40px);
  }
}

.index-grid {
  grid-template-columns: 124fr 360fr 124fr;
  grid-template-rows: auto 1fr auto auto;
  grid-template-areas: 'area-header area-header area-header' 'area-left area-main area-right' 'area-social area-terms area-service' 'area-social area-terms area-service';
}

.index-grid > * {
  @apply min-w-0 min-h-0 max-w-full;
}

@media (min-width: 1920px) {
  .index-grid {
    max-width: 1880px;
  }
}

@media (orientation: portrait) {
  .index-grid {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto 1fr auto auto;
    grid-template-areas: 'area-header area-header area-header' 'area-main area-main area-main' 'area-social area-social area-service' 'area-terms area-terms area-terms';
  }
}

.area-header {
  grid-area: area-header;
}

.area-main {
  grid-area: area-main;
}

@media (orientation: landscape) {
  .area-main-wrapper {
    max-width: 360px;
  }
}

@media (orientation: landscape) and (min-width: 768px) {
  .area-main-wrapper {
    max-width: 430px;
  }
}

@media (orientation: landscape) and (min-width: 1024px) {
  .area-main-wrapper {
    max-width: 972px;
  }
}

@media (orientation: portrait) and (min-width: 768px) {
  .area-main-wrapper {
    max-width: 80%;
  }
}

@media (min-width: 1920px) {
  .area-main {
    max-width: 972px;
  }
}

.area-h-banner {
  grid-area: area-h-banner;
}

.area-social {
  grid-area: area-social;
}

.area-service {
  grid-area: area-service;
}

.area-terms {
  grid-area: area-terms;
}

.area-main-button > * {
  width: 30%;
  max-width: 12.5rem;
}
</style>
